import { useEffect, useState } from "react";
import { Plus, Edit, Trash2, GripVertical } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { Field, Input, Textarea, Btn, Toggle, Modal } from "@/components/admin/AdminUI";
import ImageUpload from "@/components/admin/ImageUpload";

const EMPTY = { name: "", description: "", image: "", active: true, in_menu: true, order: 0 };

export default function CategoriesAdmin() {
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const { refresh } = useSite();

  const load = () => api.get("/categories?active_only=false").then((r) => setCats(r.data || []));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ ...EMPTY, order: cats.length }); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...EMPTY, ...c }); setOpen(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name) { toast.error("El nombre es obligatorio"); return; }
    const payload = { ...form, order: Number(form.order) || 0 };
    try {
      if (editing) await api.put(`/admin/categories/${editing.id}`, payload);
      else await api.post("/admin/categories", payload);
      toast.success("Categoría guardada");
      setOpen(false); load(); refresh();
    } catch { toast.error("Error al guardar"); }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar categoría?")) return;
    await api.delete(`/admin/categories/${id}`); toast.success("Eliminada"); load(); refresh();
  };

  return (
    <div>
      <header className="flex items-center justify-between mb-8">
        <h1 className="font-serif text-4xl tracking-tight">Categorías</h1>
        <Btn variant="accent" onClick={openNew} data-testid="new-category-btn"><Plus size={15} /> Nueva categoría</Btn>
      </header>

      <div className="bg-white border border-sunset-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-sunset-border text-left text-xs uppercase tracking-wider text-sunset-muted">
              <th className="px-5 py-3">Orden</th><th className="px-5 py-3">Nombre</th><th className="px-5 py-3">En menú</th>
              <th className="px-5 py-3">Activa</th><th className="px-5 py-3 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {cats.map((c) => (
              <tr key={c.id} className="border-b border-sunset-border/60 hover:bg-sunset-bg" data-testid={`category-row-${c.id}`}>
                <td className="px-5 py-4 text-sunset-muted">{c.order}</td>
                <td className="px-5 py-4 font-medium">{c.name}<span className="block text-xs text-sunset-muted font-normal">/{c.slug}</span></td>
                <td className="px-5 py-4">{c.in_menu ? "Sí" : "No"}</td>
                <td className="px-5 py-4">{c.active ? <span className="text-green-700">Activa</span> : <span className="text-sunset-muted">Inactiva</span>}</td>
                <td className="px-5 py-4"><div className="flex justify-end gap-2">
                  <button onClick={() => openEdit(c)} className="p-2 hover:text-sunset-accent" data-testid={`edit-category-${c.id}`}><Edit size={16} /></button>
                  <button onClick={() => del(c.id)} className="p-2 hover:text-red-600"><Trash2 size={16} /></button>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Editar categoría" : "Nueva categoría"}>
        <Field label="Nombre"><Input data-testid="category-name" value={form.name} onChange={(e) => set("name", e.target.value)} /></Field>
        <Field label="Descripción"><Textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} /></Field>
        <div className="mb-4"><ImageUpload label="Imagen" value={form.image} onChange={(v) => set("image", v)} /></div>
        <Field label="Orden"><Input type="number" value={form.order} onChange={(e) => set("order", e.target.value)} /></Field>
        <div className="flex gap-8 py-2 mb-4">
          <Toggle checked={form.active} onChange={(v) => set("active", v)} label="Activa" />
          <Toggle checked={form.in_menu} onChange={(v) => set("in_menu", v)} label="Mostrar en menú" />
        </div>
        <Btn variant="accent" onClick={save} data-testid="save-category-btn">Guardar</Btn>
      </Modal>
    </div>
  );
}
